import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { createPackagesStep1, updatePackagesStep1 } from '../../../axios/ServerRequest';

interface Media {
  url: string;
}
export interface PackageFormData {
  title: string;
  category: string;
  subcategory: string;
  tags: string[];
  mediaIds: string[];
  industry: string;
  country: string;
  startWorkIn: string;
  startWorkUnit: string;
  businessSize: string;
  description: string;
  primaryMediaId: string;
}

interface PackageState extends PackageFormData {
  loading: boolean;
  error: string | null;
  success: boolean;
  packageId: string | null;
  currentStep: number;
  isSubmitting: boolean;
  submitError: string | null;
}

const initialState: PackageState = {
  title: '',
  category: '',
  subcategory: '',
  tags: [],
  mediaIds: [],
  industry: '',
  country: '',
  startWorkIn: '',
  startWorkUnit: '',
  businessSize: '',
  description: '',
  primaryMediaId: '',
  loading: false,
  error: null,
  success: false,
  packageId: null,
  currentStep: 0,
  isSubmitting: false,
  submitError: null,
};

// Async thunk for creating a package
export const createPackage = createAsyncThunk(
  'package/create',
  async (
    packageData: Omit<PackageFormData, 'mediaIds' | 'primaryMediaId'>,
    { rejectWithValue },
  ) => {
    try {
      const response = await createPackagesStep1(packageData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create package');
    }
  },
);

// Async thunk for updating a package
export const updatePackage = createAsyncThunk(
  'package/update',
  async (
    { packageId, packageData }: { packageId: string; packageData: Partial<PackageFormData> },
    { rejectWithValue },
  ) => {
    try {
      const response = await updatePackagesStep1(packageId, packageData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update package');
    }
  },
);

const packageSlice = createSlice({
  name: 'package',
  initialState,
  reducers: {
    updateField: (state, action: PayloadAction<{ field: keyof PackageFormData; value: any }>) => {
      const { field, value } = action.payload;
      state[field] = value as never;
    },
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    setFormData: (state, action: PayloadAction<Partial<PackageFormData>>) => {
      return { ...state, ...action.payload, submitError: null };
    },
    setSubmitError: (state, action: PayloadAction<string | null>) => {
      state.submitError = action.payload;
      state.isSubmitting = false;
    },
    resetPackage: () => initialState,
  },
  extraReducers: (builder) => {
    // Handle create package
    builder
      .addCase(createPackage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPackage.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.packageId = action.payload.id || null;
      })
      .addCase(createPackage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Handle update package
      .addCase(updatePackage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePackage.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updatePackage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { updateField, resetPackage, setFormData, setCurrentStep, setSubmitError } =
  packageSlice.actions;
export default packageSlice.reducer;
